/* eslint-disable react/require-default-props */
import type {
  SectionSlot as SectionSlotType,
  SectionWithSlots,
  SlotLabel,
  VirtualSlot as VirtualSlotType,
} from '@youshift/shared/types';
import {
  arrangeSlots,
  dateToString,
  getCustomDateRange,
  getDifferenceInColumns,
  getDifferenceInMinutes,
  GRID_GRANULARITY,
  localeNormalizer,
  returnColor,
} from '@youshift/shared/utils';
import { useState, useMemo } from 'react';

import { useItrContext } from '../../layouts/IterationRootLayout/IterationRootLayout';
import i18n from '../../utils/i18n';
import { SectionSlotAssignment } from '../Slots/SectionSlots/SectionSlotAssignment';
import { VirtualSlotVerticalVirgueria } from '../VirtualSlot';
import {
  getAssignmentsByVirtualSlot,
  getInitialCurrentMonth,
  filterSectionsByMonth,
} from './helpers';
import { ArrangedVirtualSlot } from './types';
import { MonthNavigation } from './subcomponents/MonthNavigation';

export type VerticalVirgueriaProps = {
  end: string;
  sectionsWithSlots: SectionWithSlots[];
  selectedPeople?: Set<number>;
  showRestPeriod?: boolean;
  start: string;
};

export function VerticalVirgueria({
  end,
  sectionsWithSlots,
  selectedPeople,
  showRestPeriod,
  start,
}: VerticalVirgueriaProps) {
  const locale = localeNormalizer(i18n.language);
  const { epa } = useItrContext();

  // Initialize current month state
  const [currentMonth, setCurrentMonth] = useState(
    getInitialCurrentMonth(start, end),
  );

  // Filter sections with slots for current month
  const filteredSectionsWithSlots = useMemo(
    () => filterSectionsByMonth(sectionsWithSlots, currentMonth),
    [sectionsWithSlots, currentMonth],
  );

  // Build array of days for current month
  const days = useMemo(() => {
    const startOfMonth = new Date(
      currentMonth.getUTCFullYear(),
      currentMonth.getUTCMonth(),
      1,
    );
    const endOfMonth = new Date(
      currentMonth.getUTCFullYear(),
      currentMonth.getUTCMonth() + 1,
      0,
    );
    return getCustomDateRange(
      startOfMonth.toISOString(),
      endOfMonth.toISOString(),
      locale,
      'table',
    );
  }, [currentMonth, locale]);

  return (
    <div className="w-full">
      <MonthNavigation
        start={new Date(start)}
        end={new Date(end)}
        currentMonth={currentMonth}
        onMonthChange={setCurrentMonth}
      />
      <div
        className="grid auto-cols-auto auto-rows-min my-2"
        style={{
          // First column for day names, then one column per section
          gridTemplateColumns: `auto repeat(${filteredSectionsWithSlots.length}, 1fr)`,
          gridTemplateRows: `auto repeat(${days.length}, 1fr)`,
          // First row for header, then one row per day.
        }}
      >
        {/* Header Row */}
        {/* Top left header cell (empty) */}
        <div />
        {/* Section headers */}
        {filteredSectionsWithSlots.map(({ section }) => (
          <div
            key={`header-${section.id_section}`}
            className="border text-center font-semibold rounded-md py-2"
            style={{ backgroundColor: returnColor(section.color) }}
          >
            {section.name}
          </div>
        ))}
        {/* Rows for each day */}
        <div
          style={{
            gridRow: `span ${days.length} / span ${days.length}`,
          }}
          className="grid"
        >
          {days.map(day => (
            <div
              key={day.toISOString()}
              className="flex items-center justify-center p-2 bg-gray-400 text-white font-bold rounded-lg border mb-1 border-gray-500"
            >
              {dateToString(day, 'weekday', locale)}
            </div>
          ))}
        </div>
        {filteredSectionsWithSlots.map(
          ({ section, section_slots, virtual_slots }, sectionIdx) => {
            const rows = arrangeSlots<SectionSlotType>(
              Object.values(section_slots),
            );
            const assignmentsByVirtualSlot = getAssignmentsByVirtualSlot(
              epa,
              Object.values(virtual_slots),
            );
            // virtual slots are non overlapping so we can just take the first (and only) row
            const virtualSlotsColumn = arrangeSlots<VirtualSlotType>(
              Object.values(virtual_slots),
            )[0];
            const arrangedVirtualSlots: ArrangedVirtualSlot[] =
              virtualSlotsColumn
                ? Object.values(virtualSlotsColumn).map(slot => {
                    const diffFromStart = getDifferenceInColumns(
                      days[0],
                      slot.start,
                    );
                    const duration = getDifferenceInColumns(
                      slot.start,
                      slot.end,
                    );
                    const { id_virtual_slot, min_need, max_need } = slot;
                    return {
                      start_position:
                        diffFromStart === 0 ? diffFromStart + 1 : diffFromStart,
                      end_position: diffFromStart + duration,
                      start_time: dateToString(slot.start, 'hh:mm', locale),
                      end_time: dateToString(slot.end, 'hh:mm', locale),
                      duration_minutes: getDifferenceInMinutes(
                        slot.start,
                        slot.end,
                      ),
                      id_section_slots: slot.section_slots,
                      id_virtual_slot,
                      min_need,
                      max_need,
                      spanning: false,
                    };
                  })
                : [];
            return (
              <div
                key={`section-${section.id_section}`}
                className={`grid px-1 ${sectionIdx !== filteredSectionsWithSlots.length - 1 ? 'border-r' : ''}`}
                style={{
                  gridRow: `span ${days.length} / span ${days.length}`,
                  gridTemplateColumns: `auto repeat(${rows.length}, 1fr)`,
                }}
              >
                <div
                  key={`${section.id_section}-slots`}
                  className="grid"
                  style={{
                    gridTemplateRows: `repeat(${24 * GRID_GRANULARITY * days.length}, 1fr)`,
                  }}
                >
                  {arrangedVirtualSlots.map((arrangedVirtualSlot, idx) => {
                    const nextIsContiguous =
                      arrangedVirtualSlots[idx + 1]?.start_position ===
                      arrangedVirtualSlot.end_position;
                    const currentlyAssigned =
                      assignmentsByVirtualSlot?.[
                        arrangedVirtualSlot.id_virtual_slot
                      ] || 0;
                    return (
                      <VirtualSlotVerticalVirgueria
                        slot={arrangedVirtualSlot}
                        sectionColor={section.color}
                        currentlyAssigned={currentlyAssigned}
                        nextIsContiguous={nextIsContiguous}
                      />
                    );
                  })}
                </div>
                {rows.map((row, rowIndex) => (
                  <div
                    key={`row-${rowIndex}`}
                    className="grid"
                    style={{
                      gridTemplateRows: `repeat(${24 * GRID_GRANULARITY * days.length}, 1fr)`,
                    }}
                  >
                    {row.map(slot => {
                      // TODO
                      const isSlotFull = false;

                      return (
                        <SectionSlotAssignment
                          isSlotFull={Boolean(isSlotFull)}
                          key={slot.id_section_slot}
                          sectionColor={section.color}
                          selectedPeople={selectedPeople || new Set<number>()}
                          showRestPeriod={Boolean(showRestPeriod)}
                          slot={slot}
                          week={days}
                          isVertical
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
            );
          },
        )}
      </div>
    </div>
  );
}
